// File: src/pages/Users.tsx
import React, { useState, useEffect, useCallback } from 'react';

// ☑️ Only this one
import UserDialog from '@/components/users/UserDialog';

import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  fetchBranches,
} from '@/api';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Users as UsersIcon,
  Pencil,
  Trash2,
  UserPlus,
  Search as SearchIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import type { Branch } from '@/types/branch';

type Role = 'admin' | 'branch';

interface User {
  id: string;
  name: string;
  email: string;
  role: Role;
  branch_id?: string | null;
}

interface UserFormValues {
  name: string;
  email: string;
  password?: string;
  role: Role;
  branch_id?: string | null;
}

export default function UsersPage() {
  useEffect(() => {
    document.title = 'Users - Vixalor';
  }, []);
  const [users, setUsers] = useState<User[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');

  const loadData = useCallback(async () => {
    try {
      const [usersRes, branchesRes] = await Promise.all([fetchUsers(), fetchBranches()]);
      setUsers(
        usersRes.data
          .filter((u: { role: string }) => u.role === 'admin' || u.role === 'branch')
          .map((u: { role: string; [key: string]: any }) => ({
            ...u,
            role: u.role as Role,
          }))
      );
      setBranches(branchesRes.data);
    } catch (err: unknown) {
      toast.error('Failed to load users or branches');
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setDialogMode('add');
    setSelectedUser(null);
    setUserDialogOpen(true);
  };
  const handleEdit = (user: User) => {
    setDialogMode('edit');
    setSelectedUser(user);
    setUserDialogOpen(true);
  };
  const handleDelete = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedUser) return;
    try {
      await deleteUser(selectedUser.id);
      toast.success('User deleted');
      setDeleteDialogOpen(false);
      loadData();
    } catch (err: any) {
      toast.error('Delete failed');
    }
  };

  const handleSubmit = async (data: UserFormValues) => {
    try {
      const payload: Record<string, unknown> = {
        ...data,
        branch_id: data.branch_id ?? null,
      };
      if (!data.password) {
        delete payload.password;
      }
      if (dialogMode === 'add') {
        await createUser(payload);
        toast.success('User created');
      } else if (selectedUser) {
        await updateUser(selectedUser.id, payload);
        toast.success('User updated');
      }
      setUserDialogOpen(false);
      loadData();
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message;
      toast.error(apiMessage ? `Save failed: ${apiMessage}` : 'Save failed');
    }
  };

  const getBranchName = (branchId?: string | null) => {
    return branchId
      ? branches.find(b => b.id === branchId)?.name || 'Unknown'
      : 'N/A';
  };

  // Now always strictly 'admin' or 'branch'
  const dialogDefaultValues = selectedUser || undefined;

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Users</h1>
        <p className="text-muted-foreground mt-1">Manage user accounts and permissions.</p>
      </div>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <UsersIcon className="h-5 w-5 text-primary" />
            <CardTitle>User Management</CardTitle>
          </div>
          <CardDescription>Add, edit, and manage your system users</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2">
              <SearchIcon className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-[300px]"
              />
            </div>
            <Button onClick={handleAdd}>
              <UserPlus className="mr-2 h-4 w-4" /> Add User
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map(user => (
                <TableRow key={user.id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell className="capitalize">{user.role}</TableCell>
                  <TableCell>{getBranchName(user.branch_id)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(user)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(user)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <UserDialog
        open={userDialogOpen}
        onOpenChange={setUserDialogOpen}
        onSubmit={handleSubmit}
        defaultValues={dialogDefaultValues}
        mode={dialogMode}
        branches={branches}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedUser?.name}? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
